import { Text } from '@components/ui';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import ListSubheader from '@mui/material/ListSubheader';
import MenuItem from '@mui/material/MenuItem';
import type { ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import {
  SelectValidator,
  TextValidator,
} from 'react-material-ui-form-validator';

import type {
  HireCategory,
  ScrappedItemDetails,
} from './updateHirePriceFieldsTypes';

interface UpdateHirePriceFieldsProps {
  selectedOption: { category: string | number; subCategory: string | number };
  handleChange: (event: { target: { value: unknown } }) => void;
  allCategories: HireCategory[];
  itemIdDataObject: ScrappedItemDetails | undefined;
  handleItemDataChangeObject: (event: ChangeEvent<HTMLInputElement>) => void;
  buyerCostState: number | null;
  isBuyerCostLoading: boolean;
  buyerCostError: string;
}

export function UpdateHirePriceFields({
  selectedOption,
  handleChange,
  allCategories,
  itemIdDataObject,
  handleItemDataChangeObject,
  buyerCostState,
  isBuyerCostLoading,
  buyerCostError,
}: UpdateHirePriceFieldsProps): JSX.Element {
  const { t } = useTranslation();

  return (
    <>
      <Text
        variant="label"
        sx={{
          mt: 1.25,
          mb: 1.25,
          display: 'block',
          textTransform: 'uppercase',
          fontWeight: 700,
        }}
      >
        {t('hire.updateHire.priceCalculator')}
      </Text>
      <Grid
        container
        item
        xs={12}
        sm={12}
        md={12}
        lg={12}
        spacing={2}
        style={{ display: 'flex' }}
      >
        <Grid item xs={12} sm={12} md={12} lg={12}>
          <SelectValidator
            sx={{
              width: '100%',
              borderRadius: '5px',
              backgroundColor: 'white',
              '& fieldset': { border: 'none' },
            }}
            value={JSON.stringify(selectedOption)}
            onChange={handleChange}
            label={t('hire.updateHire.selectCategorySubcategory')}
            inputProps={{
              name: 'grouped-select',
              id: 'grouped-select',
            }}
          >
            {allCategories.reduce<JSX.Element[]>(
              (listItems, category) => [
                ...listItems,
                <ListSubheader key={category.id} {...{ value: category.id }}>
                  {category.name}
                </ListSubheader>,
                ...(category.subCategories ?? []).map((subCategory) => (
                  <MenuItem
                    key={subCategory.id}
                    value={JSON.stringify({
                      category: category.id,
                      subCategory: subCategory.id,
                    })}
                  >
                    <Box sx={{ pl: 1 }}>{subCategory.name}</Box>
                  </MenuItem>
                )),
              ],
              []
            )}
          </SelectValidator>
        </Grid>
        <Grid item xs={12} sm={12} md={6} lg={6}>
          <TextValidator
            sx={{
              width: '100%',
              borderRadius: '5px',
              backgroundColor: 'white',
              '& fieldset': { border: 'none' },
            }}
            label={t('hire.updateHire.askingPriceOfVehicle')}
            onChange={handleItemDataChangeObject}
            type="number"
            name="askingPrice"
            value={
              itemIdDataObject?.askingPrice
                ? String(itemIdDataObject.askingPrice)
                : ''
            }
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        <Grid item xs={12} sm={12} md={6} lg={6}>
          <TextValidator
            sx={{
              width: '100%',
              borderRadius: '5px',
              backgroundColor: 'white',
              '& fieldset': { border: 'none' },
            }}
            disabled
            type="text"
            name="byerCost"
            value={buyerCostState === null ? '' : `$${buyerCostState}`}
            placeholder={
              isBuyerCostLoading
                ? t('hire.updateHire.calculating')
                : t('hire.updateHire.awaitingQuote')
            }
            InputLabelProps={{ shrink: true }}
          />
          {buyerCostError ? (
            <Text as="span" tone="primary" sx={{ fontSize: 12 }}>
              {buyerCostError}
            </Text>
          ) : null}
        </Grid>
      </Grid>
    </>
  );
}
